/**
 * Thin wrapper over `cordova-plugin-chooser` (native document/file picker —
 * Files app, iCloud Drive, Google Drive, Photos, etc., not just the
 * camera-plugin's photo library) for the DomiHealth "upload from storage"
 * feature.
 *
 * `chooser.getFile()` hands back the picked file's raw bytes directly
 * (`data: Uint8Array`), which is used as-is via `DomiHealthApi`'s
 * byte-upload path (`httpClient.ts`'s `httpUploadBytes`) — no
 * write-to-a-temp-file-then-re-locate-it step. An earlier version of
 * this module did write the bytes to a cache file and hand back its
 * path for `cordova-plugin-advanced-http`'s `uploadFile()`, matching how
 * `voiceRecorder.ts` handles synthesized audio, but that needs a real
 * native path back out of `cordova-plugin-file`; confirmed against a
 * real device (2026-09-13) that neither `fileEntry.toURL()` nor a
 * hand-constructed `cacheDirectory`-relative path reliably produced one
 * `uploadFile()` could actually read — so this keeps the bytes in
 * memory and uploads them directly instead.
 *
 * Native path confirmed working on a real device; camera.ts's
 * `takePicture()` and voiceRecorder.ts remain genuinely native-only
 * (their underlying plugins have no browser platform implementation at
 * all, or an explicitly unsupported one — confirmed in each plugin's
 * own source). `cordova-plugin-chooser`'s `plugin.xml` declares only
 * `android`/`ios` platforms, no `browser` one — but unlike
 * cordova-plugin-advanced-http/cordova-plugin-media (whose plugin.xml
 * genuinely omits a browser `<js-module>` when there's no browser
 * platform block), Cordova still bundles `cordova-plugin-chooser`'s
 * JS wrapper into a `cordova build browser` build regardless,
 * confirmed directly in a real build's `cordova_plugins.js` — so
 * `window.chooser` exists there as a real, non-undefined object.
 * Calling `chooser.getFile()` still fails, since it calls
 * `cordova.exec()` for a command with no browser-platform handler
 * registered at all — confirmed live (2026-09-19) as "Missing Command
 * Error" (that exact string traced to `cordova-browser`'s own
 * `exec.js`), not the `typeof chooser === 'undefined'` case this
 * module originally, incorrectly, assumed was the only way native
 * chooser support could be "unavailable". `isChooserAvailable` also
 * excludes the Cordova "browser" platform explicitly now, the same
 * way `httpClient.ts`'s `isNativeHttpAvailable` already does for the
 * same underlying reason.
 *
 * Picking a file has a genuine, simple browser-native equivalent
 * regardless — a plain `<input type="file">` — so this module falls
 * back to that instead of just throwing, letting the upload feature
 * actually be exercised on `cordova build browser` and in a plain
 * browser dev build alike.
 */

declare const chooser: {
  getFile(accept?: string): Promise<
    | undefined
    | {
        data: Uint8Array;
        dataURI: string;
        mediaType: string;
        name: string;
        uri: string;
      }
  >;
};

function isChooserAvailable(): boolean {
  if (typeof chooser === 'undefined') {
    return false;
  }
  const w = window as any;
  // See this module's docstring: cordova-plugin-chooser's JS wrapper
  // loads on the Cordova "browser" platform even though it has no
  // real implementation there, so `chooser` being defined isn't
  // sufficient on its own.
  return w.cordova?.platformId !== 'browser';
}

export interface PickedFile {
  data: Uint8Array;
  name: string;
  mediaType: string;
}

/** Browser fallback for `cordova-plugin-chooser` using a plain
 * `<input type="file">` — every browser supports this natively, no
 * plugin needed.
 *
 * NOT a perfect equivalent: `chooser.getFile()` resolves `undefined`
 * on cancellation, but a browser's native file dialog fires no
 * cancel event at all — there's no reliable "the user cancelled"
 * signal in the DOM. This approximates one: if the window regains
 * focus (the dialog closed) shortly after opening and `change` never
 * fired, it's treated as a cancellation. That's a heuristic, not a
 * guarantee, but is the standard, widely-used approach for this exact
 * gap and is good enough for this dev/testing path (the real target
 * platform is native, where `chooser.getFile()` is exact). */
function pickFileViaBrowserInput(): Promise<PickedFile | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    // NOT display: none -- confirmed to matter live (2026-09-19):
    // "upload from storage" silently did nothing at all (no error, no
    // picker) specifically in a real browser/PWA context. Some WebKit
    // versions treat a fully display:none element as not genuinely
    // interactive and silently ignore a synthetic .click() on it,
    // rather than opening the native picker. Kept present in the
    // layout but visually and interactively invisible instead — the
    // standard technique other file-upload libraries use for exactly
    // this cross-browser gap.
    Object.assign(input.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      border: 'none',
      padding: '0',
    });

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('focus', onWindowFocus);
      input.remove();
    };

    const onWindowFocus = () => {
      // Give 'change' a chance to fire first if a file really was picked.
      setTimeout(() => {
        if (!settled && (!input.files || input.files.length === 0)) {
          settled = true;
          cleanup();
          resolve(null);
        }
      }, 300);
    };

    input.onchange = () => {
      settled = true;
      window.removeEventListener('focus', onWindowFocus);
      const file = input.files?.[0];
      if (!file) {
        cleanup();
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        cleanup();
        resolve({
          data: new Uint8Array(reader.result as ArrayBuffer),
          name: file.name,
          mediaType: file.type || 'application/octet-stream',
        });
      };
      reader.onerror = () => {
        cleanup();
        reject(new Error('Failed to read the selected file.'));
      };
      reader.readAsArrayBuffer(file);
    };

    window.addEventListener('focus', onWindowFocus);
    document.body.appendChild(input);
    input.click();
  });
}

/** Opens the native file picker (Files/iCloud Drive/Google Drive/Photos,
 * depending on platform) restricted to images and PDFs — DHealth's OCR
 * pipeline accepts both. Resolves `null` if the user cancels rather than
 * throwing, since cancellation isn't an error. Falls back to a browser
 * `<input type="file">` when `cordova-plugin-chooser` isn't available
 * (any browser context — plain dev server or `cordova build browser`). */
export async function pickFileForUpload(): Promise<PickedFile | null> {
  if (!isChooserAvailable()) {
    return pickFileViaBrowserInput();
  }
  const picked = await chooser.getFile('image/*,application/pdf');
  if (!picked) {
    return null; // user cancelled
  }
  return { data: picked.data, name: picked.name, mediaType: picked.mediaType };
}
